import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MAT_DATE_FORMATS, MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacyRadioModule as MatRadioModule } from '@angular/material/legacy-radio';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatLegacyTableModule as MatTableModule } from '@angular/material/legacy-table';
import { MatLegacyTabsModule as MatTabsModule } from '@angular/material/legacy-tabs';
import { MatLegacyPaginatorModule as MatPaginatorModule } from '@angular/material/legacy-paginator';
import { MatSortModule } from '@angular/material/sort';
import { CurrencyMaskModule } from 'ng2-currency-mask';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MatToolbarModule } from '@angular/material/toolbar';
import {
  MAT_LEGACY_AUTOCOMPLETE_SCROLL_STRATEGY as MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
  MatLegacyAutocompleteModule as MatAutocompleteModule,
} from '@angular/material/legacy-autocomplete';
import { Overlay } from '@angular/cdk/overlay';
import { MatLegacyCardModule as MatCardModule } from '@angular/material/legacy-card';
import { MatLegacyChipsModule as MatChipsModule } from '@angular/material/legacy-chips';
import { MentionModule } from 'angular-mentions';

import moment from 'moment';
import { NgxMapboxGLModule } from 'ngx-mapbox-gl';
import { TreoAutogrowModule } from 'tsui/@treo';
import { TreoFindByKeyPipeModule } from 'tsui/@treo';
import { TreoNavigationModule } from 'tsui/@treo';
import { TreoScrollbarModule } from 'tsui/@treo';
import { TreoDrawerModule } from 'tsui/@treo';
import { TreoCardModule } from 'tsui/@treo';
import { TreoDateRangeModule } from 'tsui/@treo';

import { SharedModule } from 'app/shared/shared.module';
import { CalculatorModule } from 'app/layout/common/calculator/calculator.module';
import { projectsRoutes } from './projects.routing';
import { ProjectsComponent } from './projects.component';
import { ProjectsListTableComponent } from './list/table/table.component';
import { ProjectsListComponent } from './list/list.component';
import { ProjectsListGridComponent } from './list/grid/grid.component';
import { ProjectsListKanbanComponent } from './list/kanban/kanban.component';
import { ProjectsListMapComponent } from './list/map/map.component';
import { ProjectsDetailsComponent } from './details/details.component';
import { ProjectsHeaderComponent } from './details/header/header.component';
import { ProjectsListHeaderComponent } from './list/header/header.component';
import { ProjectAddComponent } from './modals/project-add/project-add.component';
import { ProjectAddChildComponent } from './modals/project-add-child/project-add-child.component';
import { ProjectAddContactComponent } from './modals/project-add-contact/project-add-contact.component';
import { PipelineAddComponent } from './modals/pipeline-add/pipeline-add.component';
import { ProjectAddAddressComponent } from './modals/project-add-address/project-add-address.component';
import { ProjectChangeTemplateComponent } from './modals/project-change-template/project-change-template.component';
import { ProjectTemplateSaveComponent } from './modals/project-template-save/project-template-save.component';
import { ProjectShowDealEmailComponent } from './modals/project-show-deal-email/project-show-deal-email.component';
import { ProjectsFilterComponent } from './modals/projects-filter/projects-filter.component';
import { ProjectsFilterSaveComponent } from './modals/projects-filter-save/projects-filter-save.component';
import { ProjectMarkAcquiredComponent } from './modals/project-mark-acquired/project-mark-acquired.component';
import { ProjectGenerateUnderwritingModelComponent } from './modals/project-generate-underwriting-model/project-generate-underwriting-model.component';
import { ProjectsSettingsComponent } from './list/settings/settings.component';
import { ProjectsSidebarComponent } from './list/sidebar/sidebar.component';
import { ProjectEditPipelineComponent } from './modals/project-edit-pipeline/project-edit-pipeline.component';
import { ProjectChangeLayoutsComponent } from './modals/project-change-layouts/project-change-layouts.component';
import { ProjectChangeStatusComponent } from 'app/modules/projects/modals/project-change-status/project-change-status.component';
import { ProjectSharingComponent } from './modals/project-sharing/project-sharing.component';
import { ProjectDialogModule } from './project-dialog/project-dialog.module';
import { ProjectPdfModule } from './project-pdf/project-pdf.module';
import { ProjectMapAddModule } from './project-map-add/project-map-add.module';
import { PipelineUserAssociationsComponent } from './list/user-associations/pipeline-user-associations.component';
import { ProjectStatusRequiredFieldsComponent } from './modals/project-status-required-fields/project-status-required-fields.component';
import { ProjectAddCompanyComponent } from './modals/project-add-company/project-add-company.component';
import { PipesModule } from 'app/shared/pipes/pipes.module';
import { CustomFormModule } from 'app/shared/modules/custom-form/custom-form.module';
import { PipelineService } from './pipeline.service';
import { DigitOnlyModule } from '@uiowa/digit-only';
import { FieldLayoutEditModule } from 'app/shared/components/field-layout-edit/field-layout-edit.module';
import { environment } from '@env/environment';
import { ProjectsSharedModule } from './@shared/projects-shared.module';
import { ProjectsListService } from './list/list.service';
import { MapDealListComponent } from './list/map/components/map-deal-list/map-deal-list.component';
import {
  TSIconModule,
  TsOverlayModule,
  TSTreeMenuModule,
  TsBadgeModule,
  TsBreadcrumbModule,
  TsButtonModule,
  TsPageHeaderModule,
} from 'tsui';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { DealsSidebarComponent } from './list/sidebar/deals-sidebar/deals-sidebar.component';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { TsSkeletonComponent } from 'tsui/lib/ts-skeleton/ts-skeleton.component';
import { LinkableRelatedToFieldCell } from '@shared/components/editable/data-table-linked-field.component';
import { TsTableGroupedComponent } from 'tsui/lib/ts-table-grouped';

@NgModule({
  declarations: [
    ProjectsComponent,
    ProjectsListComponent,
    ProjectsDetailsComponent,
    ProjectsHeaderComponent,
    ProjectAddComponent,
    ProjectAddChildComponent,
    ProjectAddContactComponent,
    ProjectAddCompanyComponent,
    ProjectAddAddressComponent,
    ProjectTemplateSaveComponent,
    ProjectsSettingsComponent,
    ProjectsSidebarComponent,
    PipelineAddComponent,
    ProjectsFilterComponent,
    ProjectsFilterSaveComponent,
    ProjectSharingComponent,
    ProjectsListTableComponent,
    ProjectsListGridComponent,
    ProjectsListKanbanComponent,
    ProjectsListMapComponent,
    ProjectEditPipelineComponent,
    ProjectChangeLayoutsComponent,
    ProjectChangeStatusComponent,
    ProjectMarkAcquiredComponent,
    ProjectChangeTemplateComponent,
    ProjectShowDealEmailComponent,
    PipelineUserAssociationsComponent,
    ProjectStatusRequiredFieldsComponent,
    ProjectGenerateUnderwritingModelComponent,
    MapDealListComponent,
    DealsSidebarComponent,
  ],
  exports: [],
  imports: [
    CommonModule,
    RouterModule.forChild(projectsRoutes),
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatDividerModule,
    MatFormFieldModule,
    MatTabsModule,
    CurrencyMaskModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatMomentDateModule,
    MatPaginatorModule,
    ProjectsListHeaderComponent,
    TreoCardModule,
    MatToolbarModule,
    MatProgressBarModule,
    MatRadioModule,
    MatRippleModule,
    MatSortModule,
    MatSelectModule,
    CalculatorModule,
    MatButtonToggleModule,
    MatSidenavModule,
    MatTableModule,
    ProjectDialogModule,
    ProjectPdfModule,
    ProjectMapAddModule,
    MatTooltipModule,
    MatAutocompleteModule,
    TreoAutogrowModule,
    MatProgressSpinnerModule,
    TreoFindByKeyPipeModule,
    TreoNavigationModule,
    TreoScrollbarModule,
    TreoDateRangeModule,
    SharedModule,
    DragDropModule,
    FormsModule,
    TreoDrawerModule,
    MentionModule,
    MatCardModule,
    MatChipsModule,
    PipesModule,
    CustomFormModule,
    DigitOnlyModule,
    FieldLayoutEditModule,
    ProjectsSharedModule,
    TSTreeMenuModule,
    TsButtonModule,
    TSIconModule,
    TsPageHeaderModule,
    TsOverlayModule,
    TsBadgeModule,
    MatButtonToggleModule,
    TSIconModule,
    TsButtonModule,
    MatIconModule,
    TsDialogComponent,
    TsBreadcrumbModule,
    AgGridModule,
    AgGridAngular,
    TsTableGroupedComponent,
    TsSkeletonComponent,
    NgxMapboxGLModule.withConfig({ accessToken: environment.mapbox.accessToken }),
    LinkableRelatedToFieldCell,
  ],
  providers: [
    ProjectsListService,
    PipelineService,
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: moment.ISO_8601,
        },
        display: {
          dateInput: 'LL',
          monthYearLabel: 'MMM YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'MMMM YYYY',
        },
      },
    },
    {
      provide: MAT_AUTOCOMPLETE_SCROLL_STRATEGY,
      useFactory: (overlay: Overlay) => {
        return () => overlay.scrollStrategies.block();
      },
      deps: [Overlay],
    },
  ],
})
export class ProjectsModule {}
